// Adapted from apps/admin/src/pages/dashboard/gps-tracking/model/useGpsTrackingData.ts (PRD #875):
// the trucks come from GovDash.Routes across every MVO in the auditor's area, grouped with their
// job summary, and the depots and disposal sites from the GovDash lists.
import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { ConnectProto, ErrorCode, type ApiError } from "@racoon/api";
import { toTimestamp } from "@racoon/lib";
import {
  useDepotsQuery,
  useDisposalSiteLocationsQuery,
  useGetGovTruckTravelPathQuery,
  useRoutesQuery,
  useTruckByIdQuery,
} from "~/shared/api/govDash/govDash.api";
import { buildStepPins, numberRouteJobs } from "~/features/map-view/model/helpers/step-pin-items";
import { getDriverName, travelPathToGpsPoints } from "../lib/utils";
import { buildFleet, routeTruckId, searchFleet, type FleetTruck } from "./fleet";
import type { MapTruck } from "./types";

export interface UseGpsTrackingDataOptions {
  start: Date;
  end: Date;
  /** The district / MVO filter, applied by the backend. */
  districtOrganizationIds: bigint[];
  serviceProviderIds: bigint[];
  searchQuery: string;
  selectedVehicleId?: string;
}

const EMPTY_ROUTES: ConnectProto.Route[] = [];

const isNotFound = (error: unknown) =>
  (error as Partial<ApiError> | undefined)?.code === ErrorCode.ERROR_CODE_NOT_FOUND;

export function useGpsTrackingData({
  start,
  end,
  districtOrganizationIds,
  serviceProviderIds,
  searchQuery,
  selectedVehicleId,
}: UseGpsTrackingDataOptions) {
  const fromTo = useMemo(() => ({ from: toTimestamp(start), to: toTimestamp(end) }), [start, end]);

  // One request for the list and the selected truck alike. Without a list_filter the
  // backend returns the whole period unpaged, like admin's ListRoutes.
  const routesQuery = useRoutesQuery({
    filter: { districtOrganizationIds, serviceProviderIds },
    datetimeFilter: fromTo,
  });
  // currentData, not data: a new period or filter must not keep showing the last one's trucks.
  const allRoutes = routesQuery.currentData?.routes ?? EMPTY_ROUTES;

  const fleet = useMemo(() => buildFleet(allRoutes), [allRoutes]);
  const vehicles = useMemo(() => searchFleet(fleet, searchQuery), [fleet, searchQuery]);

  const fleetTruck: FleetTruck | undefined = useMemo(
    () => fleet.find((truck) => truck.id === selectedVehicleId),
    [fleet, selectedVehicleId],
  );

  // A truck opened by link may have no routes in the period or be hidden by the filter.
  // Its track still plays; its card says which truck it is.
  const isOffList =
    Boolean(selectedVehicleId) && routesQuery.currentData !== undefined && !fleetTruck;
  const { currentData: offListStats } = useTruckByIdQuery(
    isOffList ? { id: BigInt(selectedVehicleId!) } : skipToken,
  );
  const offListTruck = isOffList ? offListStats?.truck : undefined;

  const selectedVehicle = useMemo<MapTruck | null>(() => {
    if (!selectedVehicleId) return null;
    return (
      fleetTruck?.truck ??
      offListTruck?.truck ?? {
        id: BigInt(selectedVehicleId),
        plateNumber: "",
        type: ConnectProto.TruckType.UNDEFINED,
      }
    );
  }, [selectedVehicleId, fleetTruck, offListTruck]);

  const routes = useMemo(
    () =>
      selectedVehicleId
        ? allRoutes.filter((route) => routeTruckId(route) === selectedVehicleId)
        : EMPTY_ROUTES,
    [allRoutes, selectedVehicleId],
  );

  const { currentData: depotsResp } = useDepotsQuery(
    selectedVehicleId ? { districtOrganizationIds: [], serviceProviderIds: [] } : skipToken,
  );
  const { currentData: disposalSitesResp } = useDisposalSiteLocationsQuery(
    selectedVehicleId ? {} : skipToken,
  );

  const { usedPickupLocations, stepPins, usedDepots } = useMemo(() => {
    const pickupLocations = new Map<bigint, ConnectProto.PickupLocation>();
    const depotIds = new Set<bigint>();
    routes.forEach((route) => {
      route.jobs?.forEach((job) => {
        if (job.pickupLocation) pickupLocations.set(job.pickupLocation.id, job.pickupLocation);
      });
      if (route.destinationLocationId) depotIds.add(route.destinationLocationId);
    });

    const locations = [
      ...(depotsResp?.depots ?? []).flatMap((info) => (info.depot ? [info.depot] : [])),
      ...(disposalSitesResp?.locations ?? []),
    ];

    return {
      usedPickupLocations: Array.from(pickupLocations.values()),
      // One truck's day reads as one count across its trips.
      stepPins: selectedVehicleId ? buildStepPins(numberRouteJobs(routes, "day")) : undefined,
      usedDepots: locations.filter((location) => depotIds.has(location.id)),
    };
  }, [routes, depotsResp, disposalSitesResp, selectedVehicleId]);

  const travelPathQuery = useGetGovTruckTravelPathQuery(
    selectedVehicleId ? { truckId: BigInt(selectedVehicleId), fromTo } : skipToken,
  );

  const gpsPoints = useMemo(() => {
    if (!selectedVehicle) return [];
    const names = Array.from(
      new Set(
        routes
          .flatMap((route) => (route.driverOnShift ? [route.driverOnShift] : []))
          .map(getDriverName),
      ),
    ).filter(Boolean);
    return travelPathToGpsPoints(
      travelPathQuery.currentData?.geojsonFeature,
      selectedVehicle,
      names,
    );
  }, [travelPathQuery.currentData, selectedVehicle, routes]);

  return {
    vehicles,
    hasRoutes: fleet.length > 0,
    routesLoading: routesQuery.isFetching,
    routesError: Boolean(routesQuery.error),
    refetchRoutes: routesQuery.refetch,
    gpsPoints,
    trackLoading: travelPathQuery.isFetching,
    trackError: Boolean(travelPathQuery.error),
    /** The track RPC refuses a truck outside the auditor's area, e.g. from someone else's link. */
    trackNotFound: isNotFound(travelPathQuery.error),
    routes,
    selectedVehicle,
    /** Set while the selected truck is not among the period's trucks. */
    offListTruck: isOffList ? (offListTruck ?? null) : undefined,
    usedPickupLocations,
    stepPins,
    usedDepots,
  };
}
